import { Controller } from "@hotwired/stimulus"
import { loadGoogleMaps } from "../../src/shared/google_maps_loader"
import MapStyle from "src/shared/resources/map-style-light.json"

// Not clickable: the circle covers the sectors and would swallow their hover events.
const CIRCLE_OPTIONS = {
  strokeWeight: 0,
  fillColor: "#3BB3DE",
  fillOpacity: 0.1,
  clickable: false,
  zIndex: 1
}

const OTHER_SECTOR_OPTIONS = { fillOpacity: 0.3, zIndex: 2 }
const OTHER_SECTOR_HOVER_OPTIONS = { fillOpacity: 0.55 }

const POLYGON_OPTIONS = {
  strokeColor: "#00BFFF",
  fillColor: "#00BFFF"
}

export default class extends Controller {
  static targets = ["canvas", "mapSection", "zone", "coordinates", "zoom", "erase"]
  static values = {
    sectorId: Number,
    zoneId: Number,
    zoom: { type: Number, default: 12 },
    zones: { type: Array, default: [] },
    overlays: { type: Array, default: [] }
  }

  connect () {
    this.otherPolygons = []
    this.writeTimer = null
    if (this.zoneIdValue) this.initMap()
  }

  disconnect () {
    clearTimeout(this.writeTimer)
    this.sectorInfoWindow?.close()
    this.sectorInfoWindow = null
    this.clearOverlays()
    this.drawingManager?.setMap(null)
    this.drawingManager = null
    this.map = null
    this.maps = null
  }

  changeZone () {
    this.zoneIdValue = Number(this.zoneTarget.value) || 0

    if (!this.zoneIdValue) {
      this.hideMap()
      return
    }

    this.showMap()
    if (!this.map) {
      this.initMap()
      return
    }

    this.renderZoneCircle()
    this.renderOtherPolygons()
    const center = this.zoneCenter()
    if (center) this.map.setCenter(center)
  }

  erase (event) {
    event.preventDefault()
    this.coordinatesTarget.value = ""
    this.editablePolygon?.setMap(null)
    this.editablePolygon = null
    this.enableDrawing()
    this.toggleErase()
  }

  async initMap () {
    if (this.map || !this.zoneIdValue) return

    try {
      this.maps = await loadGoogleMaps(["drawing"])
      const center = this.currentCenter()

      this.map = new this.maps.Map(this.canvasTarget, {
        center,
        zoom: this.zoomValue || 12,
        styles: MapStyle,
        maxZoom: 19,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: {
          position: this.maps.ControlPosition.LEFT_TOP
        }
      })

      this.map.addListener("zoom_changed", () => {
        if (this.hasZoomTarget) this.zoomTarget.value = this.map.getZoom()
      })

      this.renderZoneCircle()
      this.renderOtherPolygons()

      if (this.parsedCoordinates().length) {
        this.renderEditablePolygon()
      } else {
        this.enableDrawing()
      }

      if (this.hasEraseTarget) {
        this.map.controls[this.maps.ControlPosition.TOP_CENTER].push(this.eraseTarget)
      }
      this.toggleErase()
    } catch (_error) {
      // Keep the empty map container if the script fails to load.
    }
  }

  enableDrawing () {
    if (!this.map || !this.maps?.drawing) return

    if (this.drawingManager) {
      this.drawingManager.setMap(this.map)
      this.drawingManager.setDrawingMode(this.maps.drawing.OverlayType.POLYGON)
      return
    }

    this.drawingManager = new this.maps.drawing.DrawingManager({
      map: this.map,
      drawingMode: this.maps.drawing.OverlayType.POLYGON,
      drawingControlOptions: {
        position: this.maps.ControlPosition.TOP_CENTER,
        drawingModes: ["polygon"]
      }
    })

    this.drawingManager.addListener("polygoncomplete", (polygon) => {
      this.writePath(polygon.getPath())
      this.drawingManager.setDrawingMode(null)
      this.drawingManager.setMap(null)
      polygon.setMap(null)
      this.renderEditablePolygon()
      this.toggleErase()
    })
  }

  renderEditablePolygon () {
    if (!this.map || !this.maps) return

    this.editablePolygon?.setMap(null)
    const path = this.parsedCoordinates()
    if (!path.length) return

    this.editablePolygon = new this.maps.Polygon({
      map: this.map,
      paths: path,
      editable: true,
      draggable: true,
      zIndex: 3,
      ...POLYGON_OPTIONS
    })

    const vertices = this.editablePolygon.getPath()
    vertices.addListener("set_at", () => this.scheduleWrite())
    vertices.addListener("insert_at", () => this.scheduleWrite())
    vertices.addListener("remove_at", () => this.scheduleWrite())
    this.editablePolygon.addListener("dragend", () => this.writePath(vertices))
  }

  renderZoneCircle () {
    if (!this.map || !this.maps) return

    this.circle?.setMap(null)
    const zone = this.selectedZone()
    const radius = Number(zone?.radius)
    if (!zone || !radius) return

    this.circle = new this.maps.Circle({
      map: this.map,
      center: { lat: Number(zone.latitude), lng: Number(zone.longitude) },
      radius: radius * 1000,
      ...CIRCLE_OPTIONS
    })
  }

  renderOtherPolygons () {
    if (!this.map || !this.maps) return

    this.clearOtherPolygons()
    this.overlaysValue.forEach((overlay) => {
      if (Number(overlay.zoneId) !== Number(this.zoneIdValue)) return
      if (this.sectorIdValue && Number(overlay.id) === Number(this.sectorIdValue)) return
      if (!Array.isArray(overlay.coordinates) || !overlay.coordinates.length) return

      const polygon = new this.maps.Polygon({
        map: this.map,
        paths: overlay.coordinates,
        editable: false,
        draggable: false,
        ...OTHER_SECTOR_OPTIONS
      })
      this.bindSectorTooltip(polygon, overlay)
      this.otherPolygons.push(polygon)
    })
  }

  // Hovering a neighbouring sector shows its name and active practitioners count next to the cursor.
  bindSectorTooltip (polygon, sector) {
    if (!sector.name) return

    polygon.addListener("mouseover", (event) => {
      polygon.setOptions(OTHER_SECTOR_HOVER_OPTIONS)
      this.openSectorInfoWindow(sector, event.latLng)
    })
    polygon.addListener("mousemove", (event) => this.sectorInfoWindow?.setPosition(event.latLng))
    polygon.addListener("mouseout", () => {
      polygon.setOptions(OTHER_SECTOR_OPTIONS)
      this.sectorInfoWindow?.close()
    })
  }

  openSectorInfoWindow (sector, position) {
    this.sectorInfoWindow ||= new this.maps.InfoWindow({ disableAutoPan: true, headerDisabled: true })
    this.sectorInfoWindow.setContent(this.sectorTooltipContent(sector))
    this.sectorInfoWindow.setPosition(position)
    this.sectorInfoWindow.open({ map: this.map, shouldFocus: false })
  }

  sectorTooltipContent (sector) {
    const content = document.createElement("div")
    const name = document.createElement("strong")
    name.textContent = sector.name
    content.append(name)

    if (sector.practitionersLabel) {
      const count = document.createElement("div")
      count.textContent = sector.practitionersLabel
      content.append(count)
    }

    return content
  }

  scheduleWrite () {
    clearTimeout(this.writeTimer)
    this.writeTimer = setTimeout(() => {
      if (this.editablePolygon) this.writePath(this.editablePolygon.getPath())
    }, 300)
  }

  writePath (vertices) {
    const coords = []
    for (let index = 0; index < vertices.getLength(); index += 1) {
      const point = vertices.getAt(index)
      coords.push({ lat: point.lat(), lng: point.lng() })
    }

    this.coordinatesTarget.value = JSON.stringify(coords)
    this.toggleErase()
  }

  parsedCoordinates () {
    const raw = this.hasCoordinatesTarget ? this.coordinatesTarget.value : ""
    if (!raw) return []

    try {
      const coords = JSON.parse(raw)
      return Array.isArray(coords) ? coords : []
    } catch (_error) {
      return []
    }
  }

  selectedZone () {
    return this.zonesValue.find((zone) => Number(zone.id) === Number(this.zoneIdValue))
  }

  zoneCenter () {
    const zone = this.selectedZone()
    if (!zone) return null

    return { lat: Number(zone.latitude), lng: Number(zone.longitude) }
  }

  currentCenter () {
    const coords = this.parsedCoordinates()
    if (coords.length && this.maps) {
      const bounds = new this.maps.LatLngBounds()
      coords.forEach((coord) => bounds.extend(coord))
      return bounds.getCenter()
    }

    return this.zoneCenter() || { lat: 46.6, lng: 2.5 }
  }

  toggleErase () {
    if (!this.hasEraseTarget) return

    this.eraseTarget.hidden = !this.parsedCoordinates().length
  }

  showMap () {
    if (this.hasMapSectionTarget) this.mapSectionTarget.hidden = false
  }

  hideMap () {
    if (this.hasMapSectionTarget) this.mapSectionTarget.hidden = true
  }

  clearOverlays () {
    this.editablePolygon?.setMap(null)
    this.circle?.setMap(null)
    this.clearOtherPolygons()
    this.editablePolygon = null
    this.circle = null
  }

  clearOtherPolygons () {
    this.sectorInfoWindow?.close()
    this.otherPolygons.forEach((polygon) => polygon.setMap(null))
    this.otherPolygons = []
  }
}
